import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Hourglass, Upload, AlertTriangle, FileText, ShieldAlert, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const GROUP_LABELS = {
  senior: "Sénior (65+)",
  mineur: "Mineur (-18)",
  jeune_adulte: "Jeune adulte (18-25)",
  etudiant: "Étudiant",
  famille_nombreuse: "Famille nombreuse",
  pmr: "PMR",
  demandeur_emploi: "Demandeur d'emploi",
};

/**
 * Carte de vérification CNI — pour bénéficier des avantages mairie (groupes citoyens).
 * Distincte du justificatif de domicile (co-voiturage), mais même UX.
 */
export default function CniVerificationCard() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [reason, setReason] = useState(null);
  const [groups, setGroups] = useState([]);
  const [hasDoc, setHasDoc] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/user/cni-status")
      .then((r) => {
        setStatus(r.data.cni_status || "none");
        setReason(r.data.cni_rejected_reason);
        setGroups(r.data.groups || []);
        setHasDoc(!!r.data.has_doc);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (user) load(); }, [user]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800 * 1024) return toast.error("Fichier trop volumineux (max 800 Ko)");
    if (!file.type.match(/^(image\/|application\/pdf)/)) return toast.error("Image ou PDF uniquement");
    setBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await api.post("/user/cni-doc", { doc_url: dataUrl });
      toast.success("CNI envoyée — en attente de validation de la mairie");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  if (!user || (user.role !== "habitant" && user.role !== "commercant")) return null;
  if (loading) return null;

  const isVerified = status === "verified";
  const isPending = status === "pending";
  const isRejected = status === "rejected";

  return (
    <div
      data-testid="cni-card"
      className={`phenix-card p-4 sm:p-5 ${
        isVerified ? "bg-plum-50 border-plum-200"
        : isPending ? "bg-sun-50/60 border-sun-200"
        : isRejected ? "bg-pink-50/60 border-pink-200"
        : "bg-white"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`shrink-0 w-11 h-11 rounded-xl grid place-items-center ${
          isVerified ? "bg-gradient-to-br from-plum-600 to-plum-700 text-white"
          : isPending ? "bg-sun-500 text-white"
          : isRejected ? "bg-pink-500 text-white"
          : "bg-plum-100 text-plum-700"
        }`}>
          {isVerified ? <Sparkles size={20} strokeWidth={2.2} />
          : isPending ? <Hourglass size={20} strokeWidth={2.2} />
          : isRejected ? <ShieldAlert size={20} strokeWidth={2.2} />
          : <FileText size={20} strokeWidth={2.2} />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-extrabold text-plum-900 text-[15px] mb-0.5">
            {isVerified ? "🎁 Avantages Citoyens activés"
            : isPending ? "CNI en cours de vérification"
            : isRejected ? "CNI refusée"
            : "Activez vos avantages Istréens"}
          </h3>
          <p className="text-[13px] text-plum-700/75 leading-snug">
            {isVerified ? (
              <>
                La mairie vous a attribué les groupes suivants. Découvrez vos avantages sur <a href="/mes-avantages" className="font-bold underline text-plum-700">/mes-avantages</a>.
              </>
            ) : isPending ? (
              <>Votre CNI a bien été reçue. La mairie validera votre profil et vous attribuera vos groupes (sénior, jeune, famille…) sous 48 h.</>
            ) : isRejected ? (
              <>Motif : <span className="font-semibold text-pink-600">{reason || "Document non conforme"}</span>. Vous pouvez en renvoyer une nouvelle.</>
            ) : (
              <>Téléchargez votre CNI (recto-verso) pour que la mairie vous attribue les avantages auxquels vous avez droit selon votre situation (sénior, ado, étudiant, famille nombreuse, demandeur d&apos;emploi…). Trajets gratuits, codes remise, places offertes.</>
            )}
          </p>

          {isVerified && groups.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="cni-groups">
              {groups.map((g) => (
                <span key={g} className="phenix-pill bg-plum-100 text-plum-700 text-[11px] inline-flex items-center gap-1">
                  <Sparkles size={10} /> {GROUP_LABELS[g] || g}
                </span>
              ))}
            </div>
          )}

          {isRejected && reason && (
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-pink-600">
              <AlertTriangle size={11} /> {reason}
            </div>
          )}

          {!isVerified && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={handleFile} disabled={busy} className="hidden" data-testid="cni-file-input" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full bg-plum-700 hover:bg-plum-800 disabled:opacity-50 text-white px-4 py-2 text-[12px] font-bold"
                data-testid="cni-upload-btn"
              >
                <Upload size={13} /> {hasDoc ? "Renvoyer ma CNI" : "Ajouter ma CNI"}
              </button>
              <span className="text-[11px] text-plum-700/55">JPG, PNG ou PDF — max 800 Ko</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

CniVerificationCard.GROUP_LABELS = GROUP_LABELS;
